import { useState, useCallback } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { translateAuthError } from "./AuthErrorTranslations";
// DISABLED: Invite codes disabled for public launch. Re-enable by uncommenting.
// import { validateInviteCode } from "./InviteCodeValidator";
import { trackAuthEvent, logError } from "@/utils/sentry";
import TurnstileWidget from "./TurnstileWidget";

const RegisterForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // DISABLED: Invite codes disabled for public launch
  // const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    // DISABLED: inviteCode: "",
  });
  const { toast } = useToast();

  const handleTurnstileVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // DISABLED: if (name === "inviteCode") setInviteCodeError(null);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Błąd",
        description: "Hasła nie są identyczne",
        variant: "destructive",
      });
      return;
    }

    // DISABLED: Invite code validation
    // if (!formData.inviteCode.trim()) {
    //   setInviteCodeError("Kod zaproszenia jest wymagany");
    //   return;
    // }

    if (!acceptedTerms) {
      setTermsError("Musisz zaakceptować regulamin, aby kontynuować");
      return;
    }

    setIsLoading(true);

    try {
      // DISABLED: Invite code pre-validation
      // const validationResult = await validateInviteCode(formData.inviteCode.trim(), "validate");
      // if (!validationResult.success) {
      //   setInviteCodeError(validationResult.message || "Nieprawidłowy kod zaproszenia");
      //   setIsLoading(false);
      //   return;
      // }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
        },
      });

      if (authError) {
        trackAuthEvent('register_failure', { errorMessage: authError.message });
        toast({
          title: "Błąd rejestracji",
          description: translateAuthError(authError.message),
          variant: "destructive",
        });
        return;
      }

      trackAuthEvent('register_success');

      if (authData.user) {
        // DISABLED: Mark invite code as used
        // const useResult = await validateInviteCode(
        //   formData.inviteCode.trim(),
        //   "use",
        //   authData.user.id
        // );
        // if (!useResult.success) {
        //   logError("Failed to mark invite code as used", new Error(useResult.message || 'Unknown'), { flow: 'register' });
        // }

        // No session means email confirmation is required
        if (!authData.session) {
          setPendingEmail(formData.email);
        }
      }
    } catch (error) {
      logError('Register unexpected error', error, { flow: 'register' });
      toast({
        title: "Błąd",
        description: "Wystąpił nieoczekiwany błąd",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!pendingEmail) return;
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: pendingEmail,
      });
      if (error) {
        toast({
          title: "Błąd",
          description: translateAuthError(error.message),
          variant: "destructive",
        });
      } else {
        toast({
          title: "Wysłano ponownie",
          description: "Sprawdź swoją skrzynkę pocztową",
        });
      }
    } catch {
      toast({
        title: "Błąd",
        description: "Nie udało się wysłać maila",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  if (pendingEmail) {
    return (
      <div className="space-y-6 text-center py-4">
        <div className="flex justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <Mail className="h-10 w-10 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">Sprawdź swoją skrzynkę</h2>
          <p className="text-sm text-muted-foreground">
            Wysłaliśmy link aktywacyjny na adres
          </p>
          <p className="text-sm font-medium text-foreground">{pendingEmail}</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Kliknij link w mailu, aby potwierdzić konto i zacząć korzystać z Viraluj.
        </p>
        <div className="pt-2 space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 rounded-xl"
            onClick={handleResendEmail}
            disabled={isResending}
          >
            {isResending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Wysyłanie...
              </>
            ) : (
              "Wyślij ponownie"
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            Nie widzisz maila? Sprawdź folder spam.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="register-email">Email</Label>
        <Input
          id="register-email"
          name="email"
          type="email"
          placeholder="twoj@email.com"
          value={formData.email}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="register-password">Hasło</Label>
        <div className="relative">
          <Input
            id="register-password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={formData.password}
            onChange={handleInputChange}
            required
            className="h-11 pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm-password">Potwierdź hasło</Label>
        <Input
          id="confirm-password"
          name="confirmPassword"
          type="password"
          placeholder="••••••••"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      {/* DISABLED: Invite code input field
      <div className="space-y-2">
        <Label htmlFor="invite-code">Kod zaproszenia</Label>
        <div className="relative">
          <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="invite-code"
            name="inviteCode"
            type="text"
            placeholder="Wpisz kod zaproszenia"
            value={formData.inviteCode}
            onChange={handleInputChange}
            className={`h-11 pl-10 ${inviteCodeError ? "border-destructive focus-visible:ring-destructive" : ""}`}
            required
          />
        </div>
        {inviteCodeError && <p className="text-sm text-destructive">{inviteCodeError}</p>}
      </div>
      */}

      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <Checkbox
            id="terms"
            checked={acceptedTerms}
            onCheckedChange={(checked) => {
              setAcceptedTerms(checked === true);
              setTermsError(null);
            }}
            className="mt-0.5"
          />
          <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
            Akceptuję{" "}
            <a href="/regulamin" target="_blank" className="text-primary underline hover:text-primary/80">Regulamin</a>
            {" "}oraz{" "}
            <a href="/polityka-prywatnosci" target="_blank" className="text-primary underline hover:text-primary/80">Politykę Prywatności</a>
          </label>
        </div>
        {termsError && <p className="text-sm text-destructive">{termsError}</p>}
      </div>

      <TurnstileWidget onVerify={handleTurnstileVerify} />

      <Button
        type="submit"
        className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-medium"
        disabled={
          isLoading ||
          !captchaToken ||
          !formData.email ||
          !formData.password ||
          !formData.confirmPassword ||
          // DISABLED: !formData.inviteCode.trim() ||
          !acceptedTerms
        }
      >
        {isLoading ? "Rejestracja..." : "Utwórz konto"}
      </Button>
    </form>
  );
};

export default RegisterForm;
